import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, timeout } from 'rxjs';
import { PensionermanagementService } from '../pensionermanagement/pensionermanagement.service';
import { AlertService } from './alert.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandlerService } from './error-handler.service';
import { Router } from '@angular/router';
import {
  DivisionTownshipNrcListResponse,
  PensionTypeListResponse,
  RelationAndReligionListResponse,
} from '../../models/data-store.model';
import { AppService } from './app.service';
import { SelectOption } from '../../models/adminmanagement/AdminRole';
import { LoadingService } from './loading.service';
import { ConstantValues } from '../../common/constant-values';

@Injectable({
  providedIn: 'root',
})
export class DataStorageService {

  currentSelectedLang: any;

  constructor(
    private pensionerService: PensionermanagementService,
    private appService: AppService,
    private alertService: AlertService,
    private errorHandler: ErrorHandlerService,
    private router: Router,
    private loadingService: LoadingService
  ) {
    this.currentSelectedLang = localStorage.getItem(ConstantValues.StaticValue.PMS_SYTSTEM_LANG);
  }

  // #region share function
  private valueCheck(data: any): boolean {
    return data !== '' && data !== null && data !== undefined;
  }

  public async callWithRetry<T>(
    apiFn: () => Observable<T>,
    options?: {
      maxRetry?: number;
      handleError?: boolean;
      fallbackValue?: T;
    }
  ): Promise<T | undefined> {
    const maxRetry = options?.maxRetry ?? 3;
    let attempts = 0;

    while (attempts < maxRetry) {
      try {
        const response = await firstValueFrom(apiFn().pipe(timeout(1000000))); // prevent hanging
        this.loadingService.hide();
        return response as T;
      } catch (error: unknown) {
        attempts++;
        this.loadingService.hide();
        if (error instanceof HttpErrorResponse) {
          if (error.status === 401) {
            await this.alertService.Error(
              'Your session has expired. Please log in again.'
            );
            this.router.navigate(['/login']);
            return undefined;
          }

          if (options?.handleError !== false) {
            // const result = await this.alertService.showError(
            //   this.errorHandler.handleError(error)
            // );
            // if (!result.isConfirmed) {
            //   return options?.fallbackValue;
            // }
            const errorCode = error.status || 'Network/CORS'; 
            const errorMessage = this.errorHandler.handleError(error);
            const finalMessage = `${errorMessage} (Error Code: ${errorCode})`;

            // 2. Use AWAIT so the code stops and waits for the user to click "OK"
            const result = await this.alertService.showError(finalMessage);

            // 3. Handle the fallback if they cancel or close the alert
            if (!result.isConfirmed) {
                return options?.fallbackValue;
            }
          }
        } else {
          console.error('Unexpected error:', error);
          await this.alertService.Error('An unexpected error occurred.');
          // Allow retry instead of returning
        }
      }
    }

    await this.alertService.Error('Maximum retry limit reached.');
    return options?.fallbackValue;
  }

  // #endregion

  // #region Pension Type Function Group
  private pensionTypeResponse!: PensionTypeListResponse;

  public getPensionData = async (): Promise<
    PensionTypeListResponse | undefined
  > => {
    // if (this.valueCheck(this.pensionTypeResponse)) {
    //   return this.pensionTypeResponse;
    // }

    const response = await this.callWithRetry(() =>
      this.pensionerService.getPensionTypeOptions()
    );

    if (response?.Result) {
      this.pensionTypeResponse = response.Result;
      return this.pensionTypeResponse;
    }

    if (response?.Message) {
      const result = await this.alertService.showError(
        response.Message
      );
      if (result.isConfirmed) {
        return await this.getPensionData();
      }
    }

    return undefined;
  };
  // #endregion

  // #region Relitionship and religions
  private relationAndReligionListResponse!: RelationAndReligionListResponse;

  public getRelationAndReligionData = async (): Promise<
    RelationAndReligionListResponse | undefined
  > => {
    // if (this.valueCheck(this.relationAndReligionListResponse)) {
    //   return this.relationAndReligionListResponse;
    // }

    const response = await this.callWithRetry(() =>
      this.pensionerService.getRelationToNRCOptions()
    );

    if (response?.Result) {
      this.relationAndReligionListResponse = response.Result;
      return this.relationAndReligionListResponse;
    }

    if (response?.Message) {
      const result = await this.alertService.showError(
        response.Message
      );
      if (result.isConfirmed) {
        return await this.getRelationAndReligionData();
      }
    }

    return undefined;
  };
  // #endregion

  // #region Ministry List
  private ministryList: SelectOption[] = [];

  public getMinistryList = async (
    pensionGroupVale: string
  ): Promise<SelectOption[]> => {
    if (this.valueCheck(pensionGroupVale)) {
      // if (this.ministryList.length > 0) {
      //   const filterTownship = this.ministryList.filter((item) => {
      //     return item.Parent.includes(pensionGroupVale);
      //   });

      //   if (filterTownship.length > 0) {
      //     return filterTownship;
      //   }
      // }
      const response = await this.callWithRetry(() =>
        this.pensionerService.getMinistryListOptions(pensionGroupVale)
      );

      if (response?.Results) {
        this.ministryList.push(...response.Results);
        return response.Results;
      }

      if (response?.Message) {
        const result = await this.alertService.showError(
          response.Message
        );
        if (result.isConfirmed) {
          return await this.getMinistryList(pensionGroupVale);
        }
      }
    }

    return [];
  };
  // #endregion

  // #region Department List
  private departmentList: SelectOption[] = [];

  public getDepartmentList = async (
    pensionGroupVale: string,
    ministryId: string
  ): Promise<SelectOption[]> => {
    if (this.valueCheck(pensionGroupVale) && this.valueCheck(ministryId)) {
      if (this.departmentList.length > 0) {
        const filterTownship = this.departmentList.filter((item) => {
          return item.Parent.includes(ministryId);
        });

        if (filterTownship.length > 0) {
          return filterTownship;
        }
      }
      const response = await this.callWithRetry(() =>
        this.pensionerService.getDepartmentListOptions(
          pensionGroupVale,
          ministryId
        )
      );

      if (response?.Result) {
        this.departmentList.push(...response.Result.Departments);
        return response.Result.Departments;
      }

      if (response?.Message && response.Message != "No record found.") {
        const result = await this.alertService.showError(
          response.Message
        );
        if (result.isConfirmed) {
          return await this.getDepartmentList(pensionGroupVale, ministryId);
        }
      }
    }

    return [];
  };
  // #endregion

  // #region Division, Township and nrc
  private divisionTownshipNrcListResponse!: DivisionTownshipNrcListResponse;

  //Get All Division And Nrc List
  public divisionNrcApi = async (): Promise<
    DivisionTownshipNrcListResponse | undefined
  > => {
    // if (
    //   this.valueCheck(this.divisionTownshipNrcListResponse) &&
    //   this.valueCheck(this.divisionTownshipNrcListResponse.AddressDivisions) &&
    //   this.valueCheck(this.divisionTownshipNrcListResponse.NRCDivisionCodes) &&
    //   this.valueCheck(this.divisionTownshipNrcListResponse.NRCNationalities)
    // ) {
    //   return this.divisionTownshipNrcListResponse;
    // }

    const response:any = await this.callWithRetry(() =>
      this.appService.getNRCData(null)
    );

    if (response.Result) {
      this.divisionTownshipNrcListResponse = response.Result;
      return this.divisionTownshipNrcListResponse;
    }

    if (response?.Message) {
      const result = await this.alertService.showError(
        response.Message
      );
      if (result.isConfirmed) {
        return await this.divisionNrcApi();
      }
    }

    return undefined;
  };

  // Nrc Township List Base On Nrc Division code
  public nrcTownshipApi = async (
    divisionCode: string, currentLang: any
  ): Promise<SelectOption[]> => {
    if (!this.valueCheck(divisionCode)) {
      return [];
    }

    try {
      // const filterTownship =
      //   this.divisionTownshipNrcListResponse.NRCTownships.filter((item) => {
      //     return item.Parent.includes(divisionCode);
      //   });

      // if (filterTownship.length > 0) {
      //   return filterTownship;
      // }

      const response = await this.callWithRetry(() =>
        this.appService.getNRCData(divisionCode)
      );

      if (response?.Result) {
        // this.currentSelectedLang == 'en' ? this.divisionTownshipNrcListResponse.NRCTownships.push(
        //   ...response.Result.NRCTownships
        // ) : this.divisionTownshipNrcListResponse.NRCTownships.push(
        //   ...response.Result.NRCTownshipsMM
        // )
        return currentLang == 'en' ? response.Result.NRCTownships : response.Result.NRCTownshipsMM;
      }

      if (response?.Message) {
        try {
          const result = await this.alertService.showError(
            response.Message
          );
          if (result.isConfirmed) {
            return await this.nrcTownshipApi(divisionCode, currentLang); // Retry
          }
        } catch (alertError) {
          console.error('Error showing alert:', alertError);
          await this.alertService.showError(
            'Something went wrong while loading NRC townships.'
          );
        }
      }
    } catch (error) {
      console.error('Error in nrcTownshipApi:', error);
      await this.alertService.showError(
        'Something went wrong while loading NRC townships.'
      );
    }

    return [];
  };

  //Address Township List base on division code
  public divisionTownshipApi = async (
    divisionCode: string
  ): Promise<SelectOption[]> => {
    if (this.valueCheck(divisionCode)) {
      // const filterTownship =
      //   this.divisionTownshipNrcListResponse.NRCTownships.filter((item) => {
      //     return item.Parent.includes(divisionCode);
      //   });

      // if (filterTownship.length > 0) {
      //   return filterTownship;
      // }

      const response = await this.callWithRetry(() =>
        this.appService.getAddressTownshipByDivision(divisionCode)
      );

      if (response?.Result) {

        // this.currentSelectedLang == 'en' ? this.divisionTownshipNrcListResponse.NRCTownships.push(
        //   // ...response.Result.NRCDivisionCodes
        //   ...response.Result.NRCDivisionCodes
        // ) : this.divisionTownshipNrcListResponse.NRCTownships.push(
        //   // ...response.Result.NRCDivisionCodes
        //   ...response.Result.NRCDivisionCodesMM
        // )
        
        // return response.Result.NRCDivisionCodes;
        return this.currentSelectedLang == 'en' ? response.Result.TownshipNameEN : response.Result.TownshipNameMM;
      }

      if (response?.StatusCode !== '000' && response?.Message) {
        const result = await this.alertService.showError(
          response.Message
        );
        if (result.isConfirmed) {
          return await this.divisionTownshipApi(divisionCode);
        }
      }
    }

    return [];
  };
  // #endregion
}
